import React, { useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  CardElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import "../../assets/styles/card.css";
import BASE_URL from "../../config";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const AddCardForm = ({ onPaymentMethodAdded, saving, currencySymbol }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [setAsDefault, setSetAsDefault] = useState(true);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) {
      toast.error("Stripe has not finished loading. Please wait a moment.");
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      toast.error("Card element is not ready.");
      return;
    }

    try {
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
      });

      if (error) {
        toast.error(error.message || "Failed to create payment method");
        return;
      }

      await onPaymentMethodAdded(paymentMethod.id, setAsDefault, "card");
      cardElement.clear();
    } catch (err) {
      console.error("Error adding payment method:", err);
      toast.error("Error adding payment method. Please try again.");
    }
  };

  return (
    <form className="payment-form-card" onSubmit={handleSubmit}>
      <label className="payment-form-label">Card Details</label>
      <div className="payment-card-element">
        <CardElement
          options={{
            hidePostalCode: true,
            style: {
              base: {
                fontSize: "16px",
                color: "#f7fafc",
                iconColor: "#f7fafc",
                fontFamily: "'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                fontSmoothing: "antialiased",
                "::placeholder": { color: "#cbd5e0" },
                ":-webkit-autofill": {
                  color: "#f7fafc",
                  textShadow: "0 0 0 #f7fafc",
                  "-webkit-text-fill-color": "#f7fafc",
                },
              },
              invalid: { color: "#f56565" },
            },
          }}
        />
      </div>
      <label className="payment-checkbox">
        <input
          type="checkbox"
          checked={setAsDefault}
          onChange={(e) => setSetAsDefault(e.target.checked)}
        />
        <span>Set as default payment method</span>
      </label>
      <button className="payment-submit-btn" type="submit" disabled={saving || !stripe}>
        {saving ? "Saving..." : `Add Card${currencySymbol ? ` (${currencySymbol})` : ""}`}
      </button>
    </form>
  );
};

const AddBankForm = ({ onBankAccountAdded, saving }) => {
  const [isRedirecting, setIsRedirecting] = useState(false);

  const handleAddBankAccount = async () => {
    setIsRedirecting(true);

    try {
      const token = localStorage.getItem("jwt_token");

      // Build success and cancel URLs
      const currentUrl = window.location.href;
      const successUrl = `${currentUrl}${currentUrl.includes('?') ? '&' : '?'}bank_setup=success`;
      const cancelUrl = `${currentUrl}${currentUrl.includes('?') ? '&' : '?'}bank_setup=cancelled`;

      const response = await fetch(`${BASE_URL}/api/subscription/payment/bacs-checkout/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          successUrl,
          cancelUrl,
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || "Failed to create checkout session");
      }

      const { checkout_url } = await response.json();

      // Redirect to Stripe Checkout
      window.location.href = checkout_url;
    } catch (err) {
      console.error("Error creating checkout session:", err);
      toast.error(err.message || "Error setting up bank account. Please try again.");
      setIsRedirecting(false);
    }
  };

  return (
    <div className="payment-form-card">
      <div className="bank-setup-info">
        <h4 className="bank-setup-title">Set up Direct Debit</h4>
        <p className="bank-setup-description">
          You'll be redirected to Stripe's secure checkout page to enter your bank details
          and authorize the Direct Debit mandate.
        </p>
      </div>

      <div className="processing-warning">
        <span className="warning-icon">i</span>
        <span>Bank account payments take 3-5 business days to process. Your subscription will activate once payment is confirmed.</span>
      </div>

      <button
        className="payment-submit-btn"
        type="button"
        onClick={handleAddBankAccount}
        disabled={saving || isRedirecting}
      >
        {isRedirecting ? "Redirecting to Stripe..." : "Add Bank Account"}
      </button>
    </div>
  );
};

function PaymentMethods() {
  const [stripePromise, setStripePromise] = useState(null);
  const [stripeConfig, setStripeConfig] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);
  const [paymentActionLoading, setPaymentActionLoading] = useState(null);
  const [paymentMethodType, setPaymentMethodType] = useState("card");

  useEffect(() => {
    const fetchStripeConfig = async () => {
      try {
        const token = localStorage.getItem("jwt_token");
        const res = await fetch(`${BASE_URL}/api/subscription/payment/config/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          throw new Error("Failed to fetch Stripe config");
        }
        const data = await res.json();
        setStripeConfig(data);
        setStripePromise(loadStripe(data.stripe_public_key));
      } catch (err) {
        console.error("Error fetching Stripe config:", err);
        toast.error("Unable to load payment settings.");
      }
    };

    const fetchPaymentMethods = async () => {
      setPaymentLoading(true);
      try {
        const token = localStorage.getItem("jwt_token");
        const res = await fetch(`${BASE_URL}/api/subscription/payment/methods/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          throw new Error("Failed to load payment methods");
        }
        const data = await res.json();
        setPaymentMethods(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error fetching payment methods:", err);
        toast.error("Unable to load payment methods.");
      } finally {
        setPaymentLoading(false);
      }
    };

    // Handle return from Stripe Checkout
    const handleBankSetupReturn = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const bankSetupStatus = urlParams.get('bank_setup');

      if (bankSetupStatus === 'success') {
        toast.success("Bank account added successfully. Verification may take 2-3 business days.");
        // Clean up URL
        urlParams.delete('bank_setup');
        const newUrl = urlParams.toString()
          ? `${window.location.pathname}?${urlParams.toString()}`
          : window.location.pathname;
        window.history.replaceState({}, '', newUrl);
      } else if (bankSetupStatus === 'cancelled') {
        toast.info("Bank account setup was cancelled.");
        // Clean up URL
        urlParams.delete('bank_setup');
        const newUrl = urlParams.toString()
          ? `${window.location.pathname}?${urlParams.toString()}`
          : window.location.pathname;
        window.history.replaceState({}, '', newUrl);
      }
    };

    fetchStripeConfig();
    fetchPaymentMethods();
    handleBankSetupReturn();
  }, []);

  const refreshPaymentMethods = async () => {
    setPaymentLoading(true);
    try {
      const token = localStorage.getItem("jwt_token");
      const res = await fetch(`${BASE_URL}/api/subscription/payment/methods/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to load payment methods");
      const data = await res.json();
      setPaymentMethods(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error refreshing payment methods:", err);
      toast.error("Unable to refresh payment methods.");
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleAddPaymentMethod = async (paymentMethodId, setAsDefault = true, type = "card") => {
    setSavingPayment(true);
    try {
      const token = localStorage.getItem("jwt_token");
      const res = await fetch(`${BASE_URL}/api/subscription/payment/methods/add/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ paymentMethodId, setAsDefault, type }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || "Failed to add payment method");
      }

      toast.success(
        type === "bacs_debit"
          ? "Bank account added successfully. Verification may take 1-2 business days."
          : "Payment method added successfully"
      );
      await refreshPaymentMethods();
    } catch (err) {
      console.error("Add payment method error:", err);
      toast.error(err.message || "Failed to add payment method.");
      throw err;
    } finally {
      setSavingPayment(false);
    }
  };

  const handleDeletePaymentMethod = async (methodId) => {
    if (!window.confirm("Remove this payment method?")) return;
    setPaymentActionLoading(methodId);
    try {
      const token = localStorage.getItem("jwt_token");
      const res = await fetch(
        `${BASE_URL}/api/subscription/payment/methods/${methodId}/`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || "Failed to delete payment method");
      }
      toast.success("Payment method removed");
      setPaymentMethods(paymentMethods.filter((m) => m.id !== methodId));
    } catch (err) {
      console.error("Delete payment method error:", err);
      toast.error(err.message || "Failed to delete payment method.");
    } finally {
      setPaymentActionLoading(null);
    }
  };

  const handleSetDefaultPaymentMethod = async (methodId) => {
    setPaymentActionLoading(methodId);
    try {
      const token = localStorage.getItem("jwt_token");
      const res = await fetch(
        `${BASE_URL}/api/subscription/payment/methods/${methodId}/set-default/`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || "Failed to set default payment method");
      }
      toast.success("Default payment method updated");
      await refreshPaymentMethods();
    } catch (err) {
      console.error("Set default payment method error:", err);
      toast.error(err.message || "Failed to set default payment method.");
    } finally {
      setPaymentActionLoading(null);
    }
  };

  const getPaymentMethodIcon = (method) => {
    if (method.type === "bacs_debit") {
      return "bank";
    }
    return method.brand?.toLowerCase() || "card";
  };

  const getPaymentMethodDisplay = (method) => {
    if (method.type === "bacs_debit") {
      let subtitle = method.sort_code ? `Sort Code: ${method.sort_code}` : "Direct Debit";
      return {
        title: `Bank Account **** ${method.last4}`,
        subtitle: subtitle,
        mandateStatus: method.mandate_status,
      };
    }
    return {
      title: `${method.brand?.toUpperCase() || "Card"} **** ${method.last4}`,
      subtitle: `Expires ${method.exp_month}/${method.exp_year}`,
      mandateStatus: null,
    };
  };

  const getMandateStatusBadge = (status) => {
    if (!status) return null;

    const statusConfig = {
      active: { label: "Active", className: "mandate-active" },
      pending: { label: "Verification in progress", className: "mandate-pending" },
      inactive: { label: "Inactive", className: "mandate-inactive" },
      refused: { label: "Refused", className: "mandate-refused" },
    };

    const config = statusConfig[status] || { label: status, className: "mandate-default" };
    return <span className={`mandate-status-badge ${config.className}`}>{config.label}</span>;
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />

      <div className="pref-card-container">
        <div className="wifi-header">
          <h2 className="pref-card-title">Payment Methods</h2>
        </div>
        <p className="pref-card-description">
          Add a card or bank account to manage subscription payments. Your default method will be used for charges.
        </p>

        {/* Payment Method Type Selector */}
        <div className="payment-type-selector">
          <button
            className={`payment-type-btn ${paymentMethodType === "card" ? "active" : ""}`}
            onClick={() => setPaymentMethodType("card")}
          >
            <span className="payment-type-icon">Card</span>
          </button>
          <button
            className={`payment-type-btn ${paymentMethodType === "bank" ? "active" : ""}`}
            onClick={() => setPaymentMethodType("bank")}
          >
            <span className="payment-type-icon">Bank Account</span>
          </button>
        </div>

        {!stripePromise || !stripeConfig ? (
          <p className="pref-card-loading">Loading payment settings...</p>
        ) : (
          <Elements stripe={stripePromise}>
            {paymentMethodType === "card" ? (
              <AddCardForm
                onPaymentMethodAdded={handleAddPaymentMethod}
                saving={savingPayment}
                currencySymbol={stripeConfig?.currency_symbol}
              />
            ) : (
              <AddBankForm
                onBankAccountAdded={refreshPaymentMethods}
                saving={savingPayment}
              />
            )}
          </Elements>
        )}

        <div className="payment-methods-list">
          <h3 className="payment-methods-list-title">Saved Payment Methods</h3>
          {paymentLoading ? (
            <p className="pref-card-loading">Loading saved payment methods...</p>
          ) : paymentMethods.length === 0 ? (
            <p className="pref-card-description">No saved payment methods yet.</p>
          ) : (
            paymentMethods.map((method) => {
              const display = getPaymentMethodDisplay(method);
              const iconType = getPaymentMethodIcon(method);

              return (
                <div key={method.id} className="wifi-item">
                  <div className="wifi-info">
                    <div className="wifi-name-header">
                      <span className={`payment-method-icon ${iconType}`}></span>
                      <h3 className="wifi-name">{display.title}</h3>
                      {method.is_default && (
                        <span className="wifi-priority-badge">Default</span>
                      )}
                      {display.mandateStatus && getMandateStatusBadge(display.mandateStatus)}
                    </div>
                    <p className="wifi-ssid">{display.subtitle}</p>
                    {display.mandateStatus === "pending" && (
                      <p className="mandate-info-text">
                        Direct Debit verification takes 2-3 business days
                      </p>
                    )}
                  </div>
                  <div className="wifi-actions">
                    {!method.is_default && (
                      <button
                        className="wifi-btn-edit"
                        disabled={paymentActionLoading === method.id}
                        onClick={() => handleSetDefaultPaymentMethod(method.id)}
                      >
                        {paymentActionLoading === method.id ? "Updating..." : "Set Default"}
                      </button>
                    )}
                    <button
                      className="wifi-btn-delete"
                      disabled={paymentActionLoading === method.id}
                      onClick={() => handleDeletePaymentMethod(method.id)}
                    >
                      {paymentActionLoading === method.id ? "Working..." : "Delete"}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </>
  );
}

export default PaymentMethods;
